import React, { useState, useEffect } from "react";
import { Tag, Copy, Check } from "lucide-react";

const COUPON_EXPIRY = new Date('2026-02-16T23:59:59-03:00').getTime();
const COUPON_CODE = "LOVERS10";

export const CouponBanner: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft());
  const [copied, setCopied] = useState(false);

  function calculateTimeLeft() {
    const diff = COUPON_EXPIRY - Date.now();
    if (diff <= 0) return null;
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const tl = calculateTimeLeft();
      if (!tl) {
        clearInterval(interval);
      }
      setTimeLeft(tl);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(COUPON_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!timeLeft) return null;

  const pad = (n: number) => String(n).padStart(2, "0");
  const timerStr = timeLeft.days > 0
    ? `${timeLeft.days}d ${pad(timeLeft.hours)}:${pad(timeLeft.minutes)}:${pad(timeLeft.seconds)}`
    : `${pad(timeLeft.hours)}:${pad(timeLeft.minutes)}:${pad(timeLeft.seconds)}`;

  return (
    <div className="w-full max-w-2xl mx-auto mb-6 md:mb-8">
      <div className="relative rounded-xl border border-[hsl(45,80%,45%/0.4)] bg-[hsl(45,80%,50%/0.08)] backdrop-blur-sm p-4 md:px-6 md:py-4 overflow-hidden">
        {/* Subtle shimmer */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[hsl(45,80%,60%/0.06)] to-transparent animate-pulse pointer-events-none" />

        {/* Desktop layout */}
        <div className="hidden md:flex items-center justify-between gap-4 relative z-10">
          <div className="flex items-center gap-3">
            <Tag className="w-5 h-5 text-[hsl(45,80%,55%)] flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-[hsl(45,80%,60%)] tracking-wide">
                🔥 FLASH SALE — 10% OFF ALL PACKAGES
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Use code at checkout:</p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed border-[hsl(45,80%,55%/0.5)] bg-[hsl(45,80%,50%/0.12)] hover:bg-[hsl(45,80%,50%/0.2)] transition-colors cursor-pointer"
            >
              <span className="font-mono font-bold text-sm text-[hsl(45,80%,65%)] tracking-widest">
                {COUPON_CODE}
              </span>
              {copied ? (
                <Check className="w-3.5 h-3.5 text-green-400" />
              ) : (
                <Copy className="w-3.5 h-3.5 text-muted-foreground" />
              )}
            </button>

            <div className="text-right">
              <p className="font-mono text-sm font-bold text-destructive tabular-nums">
                {timerStr}
              </p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Limited time only</p>
            </div>
          </div>
        </div>

        {/* Mobile layout */}
        <div className="flex flex-col items-center gap-3 md:hidden relative z-10 text-center">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-[hsl(45,80%,55%)]" />
            <p className="text-sm font-bold text-[hsl(45,80%,60%)] tracking-wide">
              🔥 10% OFF ALL PACKAGES
            </p>
          </div>

          <p className="text-xs text-muted-foreground">Use code at checkout:</p>

          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-[hsl(45,80%,55%/0.5)] bg-[hsl(45,80%,50%/0.12)] hover:bg-[hsl(45,80%,50%/0.2)] active:scale-95 transition-all cursor-pointer"
          >
            <span className="font-mono font-bold text-base text-[hsl(45,80%,65%)] tracking-widest">
              {COUPON_CODE}
            </span>
            {copied ? (
              <Check className="w-4 h-4 text-green-400" />
            ) : (
              <Copy className="w-4 h-4 text-muted-foreground" />
            )}
          </button>

          <div>
            <p className="font-mono text-sm font-bold text-destructive tabular-nums">
              Ends in: {timerStr}
            </p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Limited time only</p>
          </div>
        </div>
      </div>
    </div>
  );
};
